import type { PublishingThemeOverrides } from '../themes';
import {
  DEFAULT_PUBLISHING_THEME,
  mergePublishingTheme,
  type PublishingTheme,
  type PublishingThemeColorToken,
  type PublishingThemeFontToken,
} from '../themes';

export function resolveThemeValue<
  TSection extends keyof PublishingTheme,
  TKey extends keyof NonNullable<PublishingTheme[TSection]>,
>(theme: PublishingTheme, section: TSection, key: TKey) {
  const sectionValue = theme[section] as Record<string, unknown> | undefined;

  if (!sectionValue || typeof sectionValue !== 'object') {
    return undefined;
  }

  return sectionValue[String(key)] as NonNullable<
    PublishingTheme[TSection]
  >[TKey];
}

export function resolveColorToken(
  token: PublishingThemeColorToken | null | undefined,
  theme: PublishingTheme,
  explicitValue?: string | null,
) {
  if (token) {
    return theme.colors[token];
  }

  return explicitValue ?? DEFAULT_PUBLISHING_THEME.colors.textPrimary;
}

export function resolveFontToken(
  token: PublishingThemeFontToken | null | undefined,
  theme: PublishingTheme,
  explicitValue?: string | null,
) {
  if (token) {
    return theme.fonts[token] ?? theme.fonts.body;
  }

  return explicitValue ?? DEFAULT_PUBLISHING_THEME.fonts.body;
}

export function resolvePublishingTheme(input?: {
  occasionTheme?: PublishingTheme | null;
  documentThemeOverrides?: PublishingThemeOverrides | null;
  useThemeDefaults?: boolean | null;
}) {
  const baseTheme =
    input?.useThemeDefaults === false
      ? DEFAULT_PUBLISHING_THEME
      : (input?.occasionTheme ?? DEFAULT_PUBLISHING_THEME);

  return mergePublishingTheme(baseTheme, input?.documentThemeOverrides);
}
